import React, { useCallback, useContext, useRef } from 'react';
import CloudUploadIcon from '@mui/icons-material//CloudUpload';
import { Box, Button, styled, Typography } from '@mui/material';
import uuid from 'react-uuid';
import { MAX_DOCUMENT_SIZE_IN_MB } from 'common/constants/documents';
import {
  DRAG_DROP_TEXT,
  ENTERPRISE_VALUE_TEXT,
  FILE_SIZE_TEXT,
  OR_TEXT,
  SELECT_FILE_TEXT,
} from 'common/constants/process-management';
import { useDragAndDropFiles } from 'common/hooks/useDragAndDropFiles';
import validFileTypes from 'components/Documents/validFileTypes';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import useHandleFiles from 'services/hooks/useHandleFiles';
import theme from 'theme';
import CircularArrowUpLoader from '../CircularArrowUpLoader';
import TaskCompletedBox from '../TaskCompletedBox';
import { DocumentUploadModalProps } from './DocumentUploadModal.props';
import { TaskCompletedBoxProps } from '../TaskCompletedBox/TaskCompletedBox.props';

const PREFIX = 'DocumentUploadModal';

const classes = {
  topBox: `${PREFIX}-topBox`,
  uploadIconBox: `${PREFIX}-uploadIconBox`,
  uploadIcon: `${PREFIX}-uploadIcon`,
  uploadLink: `${PREFIX}-uploadLink`,
  bottomBox: `${PREFIX}-bottomBox`,
  gridItem: `${PREFIX}-gridItem`,
  text: `${PREFIX}-text`,
  subText: `${PREFIX}-subText`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.topBox}`]: ({ isMobile }: { isMobile: boolean }) => ({
    borderColor: theme.palette.primary[500],
    height: isMobile ? '100vw' : '18.75rem',
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    borderStyle: 'dashed',
    borderWidth: '0.125rem',
    borderRadius: '0.3rem',
  }),

  [`& .${classes.uploadIconBox}`]: {
    width: '5.5rem',
    height: '5.5rem',
    borderRadius: '50%',
    backgroundColor: 'rgba(0, 123, 255, 0.1)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },

  [`& .${classes.uploadIcon}`]: {
    color: theme.palette.primary[500],
    width: '2.3rem',
    height: '2.3rem',
  },

  [`& .${classes.uploadLink}`]: {
    color: theme.palette.primary[500],
    textDecoration: 'underline',
    textTransform: 'none',
    padding: '0',
    marginRight: '0.2rem',
  },

  [`& .${classes.bottomBox}`]: ({ isMobile }) => ({
    borderWidth: isMobile ? undefined : '0 0.125rem 0.125rem 0.125rem',
    borderStyle: 'solid',
    borderColor: theme.palette.primary[900],
    borderRadius: isMobile ? undefined : '0 0 0.5rem 0.5rem',
    backgroundColor: theme.palette.primary[900],
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    padding: '1.5rem 0',
  }),

  [`& .${classes.gridItem}`]: {
    display: 'flex',
    justifyContent: 'center',
  },

  [`& .${classes.text}`]: {
    fontSize: '1rem',
    padding: '0 2rem',
    color: theme.palette.primary[500],
    fontWeight: 'bold',
  },

  [`& .${classes.subText}`]: {
    fontSize: '0.75rem',
    padding: '0 2rem',
    color: theme.palette.primary[500],
  },
}));

const DocumentUploadModal = ({
  progress,
  setOpenSelectionModal,
  setFilesToUpload,
  isUploading,
  showUploadBox,
  setShowUploadBox,
}: DocumentUploadModalProps) => {
  const { isMobile, requestedFiles } = useContext(TaskContext) as unknown as TaskContextType;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { validateFiles } = useHandleFiles({
    validFileTypes,
    maxFileSizeAllowed: MAX_DOCUMENT_SIZE_IN_MB,
  });

  const handleNeedToUploadMore = () => {
    setShowUploadBox(true);
  };

  const handleOpenSelectionModal = useCallback(() => {
    setOpenSelectionModal(true);
  }, [setOpenSelectionModal]);

  const handleFiles = useCallback(
    (files: FileList) => {
      if (validateFiles(files)) {
        const filesArray = Array.from(files);
        setFilesToUpload(filesArray.map(file => ({ file, tmpId: uuid(), name: file.name })));
        handleOpenSelectionModal();
      }
    },
    [handleOpenSelectionModal, setFilesToUpload, validateFiles]
  );

  const { isOver, handleDragOverOrEnter, handleOnDrop, handleDragLeave } = useDragAndDropFiles({ handleFiles });

  const filesSelected = () => {
    if (fileInputRef.current?.files?.length) {
      handleFiles(fileInputRef.current.files);
    }
  };

  const multipleFilesInputClicked = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }, [fileInputRef]);

  const taskCompletedBoxProps: TaskCompletedBoxProps = {
    progress,
    requestedFiles,
    isMobile,
    handleNeedToUploadMore,
  };

  if (!showUploadBox) {
    return <TaskCompletedBox {...taskCompletedBoxProps} />;
  }

  return (
    <StyledBox mt={isMobile ? '50%' : '0'}>
      <button
        type="button"
        className={classes.topBox}
        style={{ backgroundColor: isOver ? theme.palette.grey[100] : theme.palette.white }}
        onDragOver={handleDragOverOrEnter}
        onDrop={handleOnDrop}
        onDragLeave={handleDragLeave}>
        <div>
          <Box display="flex" justifyContent="center">
            {!isUploading ? (
              <>
                <Button className={classes.uploadIconBox} onClick={multipleFilesInputClicked}>
                  <CloudUploadIcon className={classes.uploadIcon} />
                </Button>
                <input ref={fileInputRef} hidden type="file" multiple onChange={filesSelected} />
              </>
            ) : (
              <CircularArrowUpLoader arrowUpwardIconClass={classes.uploadIcon} />
            )}
          </Box>
          <Box margin="1.5rem 0" fontSize="1rem" padding="0 2rem">
            <Typography className={classes.text}>{DRAG_DROP_TEXT}</Typography>
            <Typography className={classes.text}>{OR_TEXT}</Typography>
            <Box display="flex" justifyContent="center" alignItems="center">
              <Button onClick={multipleFilesInputClicked} className={classes.uploadLink}>
                {SELECT_FILE_TEXT}
              </Button>
            </Box>
            <Typography className={classes.subText}>{FILE_SIZE_TEXT}</Typography>
          </Box>
          <Typography className={classes.text} style={{ fontWeight: 400 }}>
            {ENTERPRISE_VALUE_TEXT}
          </Typography>
        </div>
      </button>
    </StyledBox>
  );
};

export default DocumentUploadModal;
